/**
 * CampaignDetailView Component
 * Detailed view for a selected campaign with expandable sections
 */

import React from 'react';
import {
    ArrowLeft,
    Pause,
    Play,
    TrendingUp,
    BarChart3,
    AlertTriangle,
    Calendar,
    DollarSign,
    Target,
    Eye,
    MousePointer,
    ShoppingCart,
    ExternalLink,
    ChevronDown,
    ChevronRight
} from 'lucide-react';
import { useCommandCenter } from '../../contexts/CommandCenterContext';
import {
    formatCurrency,
    evaluateCampaignHealth,
    calculateBudgetPacing,
    getCampaignEndStatus,
    calculateObjectiveAwareTrend,
    extractMetricValue,
    getObjectiveConfig,
    normalizeObjective,
    HEALTH_STATUS,
    PACING_STATUS,
    getHealthStatusColor,
    getPacingStatusColor
} from '../../utils/objectiveHelpers';
import { formatNumber, formatPercent } from '../../utils/helpers';
import AudiencesSection from './AudiencesSection';
import ScheduledChangesSection from './ScheduledChangesSection';
import GoogleAdsSection from './GoogleAdsSection';

const CampaignDetailView = ({ campaign, onAction }) => {
    const {
        clearSelection,
        previousPeriodData,
        expandedSections,
        toggleSection
    } = useCommandCenter();

    const health = evaluateCampaignHealth(campaign);
    const pacing = calculateBudgetPacing(campaign);
    const endStatus = getCampaignEndStatus(campaign);
    const objective = normalizeObjective(campaign.objective);
    const objectiveConfig = getObjectiveConfig(objective);

    const metrics = campaign.currentMetrics || campaign.insights?.data?.[0] || {};
    const previousMetrics = previousPeriodData[campaign.id];

    // Calculate trends for key metrics
    const getTrend = (kpi) => {
        const current = extractMetricValue(metrics, kpi);
        const previous = previousMetrics ? extractMetricValue(previousMetrics, kpi) : null;
        if (current !== null && previous !== null) {
            return calculateObjectiveAwareTrend(current, previous, objective, kpi);
        }
        return null;
    };

    const statusConfig = {
        ACTIVE: { color: 'var(--color-success)', label: 'Active', icon: Play },
        PAUSED: { color: 'var(--color-warning)', label: 'Paused', icon: Pause },
        DELETED: { color: 'var(--color-error)', label: 'Deleted', icon: AlertTriangle },
        ARCHIVED: { color: 'var(--color-text-tertiary)', label: 'Archived', icon: Eye }
    };

    const status = statusConfig[campaign.status?.toUpperCase()] || statusConfig.ACTIVE;
    const StatusIcon = status.icon;

    return (
        <div style={{
            padding: 'var(--space-6)'
        }}>
            {/* Back Button */}
            <button
                onClick={clearSelection}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    marginBottom: 'var(--space-4)',
                    marginLeft: '-8px'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--color-primary)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--color-text-secondary)'}
            >
                <ArrowLeft size={16} />
                <span style={{ fontSize: 'var(--font-size-sm)' }}>Back to overview</span>
            </button>

            {/* Campaign Header */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--color-border)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-4)'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--space-4)'
                }}>
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            marginBottom: 'var(--space-2)'
                        }}>
                            <h1 style={{
                                margin: 0,
                                fontSize: 'var(--font-size-xl)',
                                fontWeight: 700,
                                color: 'var(--color-text-primary)'
                            }}>
                                {campaign.name}
                            </h1>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: '4px 10px',
                                background: `${status.color}15`,
                                border: `1px solid ${status.color}40`,
                                borderRadius: 'var(--radius-full)',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 600,
                                color: status.color
                            }}>
                                <StatusIcon size={12} />
                                {status.label}
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                {objectiveConfig.icon} {objectiveConfig.displayLabel}
                            </span>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                color: endStatus.isUrgent ? 'var(--color-warning)' : 'inherit'
                            }}>
                                {endStatus.icon} {endStatus.display}
                            </span>
                            {pacing.status !== PACING_STATUS.UNKNOWN && (
                                <span style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    color: getPacingStatusColor(pacing.status)
                                }}>
                                    <DollarSign size={14} />
                                    Budget: {pacing.percentage.toFixed(0)}% {pacing.message && `(${pacing.message})`}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-2)'
                    }}>
                        {campaign.status?.toUpperCase() === 'ACTIVE' ? (
                            <button
                                onClick={() => onAction?.('pause', campaign)}
                                className="btn"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'var(--color-warning-alpha)',
                                    border: '1px solid var(--color-warning)',
                                    color: 'var(--color-warning)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer'
                                }}
                            >
                                <Pause size={16} />
                                Pause
                            </button>
                        ) : (
                            <button
                                onClick={() => onAction?.('resume', campaign)}
                                className="btn"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'var(--color-success-alpha)',
                                    border: '1px solid var(--color-success)',
                                    color: 'var(--color-success)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer'
                                }}
                            >
                                <Play size={16} />
                                Resume
                            </button>
                        )}
                        <button
                            onClick={() => onAction?.('scale', campaign)}
                            className="btn"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-primary-alpha)',
                                border: '1px solid var(--color-primary)',
                                color: 'var(--color-primary)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer'
                            }}
                        >
                            <TrendingUp size={16} />
                            Scale
                        </button>
                        <button
                            onClick={() => onAction?.('analyze', campaign)}
                            className="btn"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'transparent',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-text-secondary)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer'
                            }}
                        >
                            <BarChart3 size={16} />
                            Analyze
                        </button>
                    </div>
                </div>

                {/* Health Status Banner */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-3) var(--space-4)',
                    background: `${getHealthStatusColor(health.status)}10`,
                    border: `1px solid ${getHealthStatusColor(health.status)}30`,
                    borderRadius: 'var(--radius-md)'
                }}>
                    <Target size={20} style={{ color: getHealthStatusColor(health.status) }} />
                    <div>
                        <span style={{
                            fontWeight: 600,
                            color: getHealthStatusColor(health.status)
                        }}>
                            {health.formattedValue}
                        </span>
                        <span style={{
                            marginLeft: 'var(--space-2)',
                            color: 'var(--color-text-secondary)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            {health.message}
                        </span>
                    </div>
                </div>
            </div>

            {/* Metrics Section */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--color-border)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-4)'
            }}>
                <h2 style={{
                    margin: '0 0 var(--space-4) 0',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <BarChart3 size={18} />
                    Performance Metrics
                </h2>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 'var(--space-4)'
                }}>
                    <MetricCard
                        label="Spend"
                        value={formatCurrency(extractMetricValue(metrics, 'spend') || 0)}
                        trend={getTrend('spend')}
                        icon={DollarSign}
                    />
                    <MetricCard
                        label="Revenue"
                        value={formatCurrency(extractMetricValue(metrics, 'revenue') || 0)}
                        trend={getTrend('revenue')}
                        icon={ShoppingCart}
                    />
                    <MetricCard
                        label={objectiveConfig.primaryKPI.toUpperCase()}
                        value={health.formattedValue || '--'}
                        trend={getTrend(objectiveConfig.primaryKPI)}
                        icon={Target}
                        highlight
                    />
                    <MetricCard
                        label="Impressions"
                        value={formatNumber(extractMetricValue(metrics, 'impressions') || 0)}
                        trend={getTrend('impressions')}
                        icon={Eye}
                    />
                    <MetricCard
                        label="Clicks"
                        value={formatNumber(extractMetricValue(metrics, 'clicks') || 0)}
                        trend={getTrend('clicks')}
                        icon={MousePointer}
                    />
                    <MetricCard
                        label="CTR"
                        value={`${(extractMetricValue(metrics, 'ctr') || 0).toFixed(2)}%`}
                        trend={getTrend('ctr')}
                        icon={MousePointer}
                    />
                </div>
            </div>

            {/* Expandable Sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Google Ads Section - Only for Google campaigns */}
                {campaign.platform?.toLowerCase() === 'google' && (
                    <CollapsibleSection
                        title="Google Ads Details"
                        icon="🔍"
                        isExpanded={expandedSections.googleAds}
                        onToggle={() => toggleSection('googleAds')}
                    >
                        <GoogleAdsSection campaign={campaign} />
                    </CollapsibleSection>
                )}

                {/* Ad Sets Section (includes Audiences, Placements, and Creatives) */}
                <CollapsibleSection
                    title="Ad Sets"
                    icon="📦"
                    isExpanded={expandedSections.audiences}
                    onToggle={() => toggleSection('audiences')}
                >
                    <AudiencesSection campaign={campaign} />
                </CollapsibleSection>

                {/* Scheduled Changes Section */}
                <CollapsibleSection
                    title="Scheduled Changes"
                    icon="📅"
                    isExpanded={expandedSections.scheduledChanges}
                    onToggle={() => toggleSection('scheduledChanges')}
                >
                    <ScheduledChangesSection campaign={campaign} />
                </CollapsibleSection>
            </div>
        </div>
    );
};

// Metric Card Component
const MetricCard = ({ label, value, trend, icon: Icon, highlight }) => (
    <div style={{
        padding: 'var(--space-3)',
        background: highlight ? 'var(--color-primary-alpha)' : 'var(--color-background)',
        borderRadius: 'var(--radius-md)',
        border: highlight ? '1px solid var(--color-primary)' : 'none'
    }}>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-1)'
        }}>
            <span style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                textTransform: 'uppercase'
            }}>
                {label}
            </span>
            <Icon size={14} style={{ color: highlight ? 'var(--color-primary)' : 'var(--color-text-tertiary)' }} />
        </div>
        <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-2)'
        }}>
            <span style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                color: highlight ? 'var(--color-primary)' : 'var(--color-text-primary)'
            }}>
                {value}
            </span>
            {trend && trend.direction !== 'neutral' && (
                <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: trend.color,
                    fontWeight: 500
                }}>
                    {trend.formatted}
                </span>
            )}
        </div>
    </div>
);

// Collapsible Section Component
const CollapsibleSection = ({ title, icon, isExpanded, onToggle, children }) => (
    <div style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden'
    }}>
        <button
            onClick={onToggle}
            style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-4)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left'
            }}
        >
            <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                fontSize: 'var(--font-size-md)',
                fontWeight: 600,
                color: 'var(--color-text-primary)'
            }}>
                <span>{icon}</span>
                {title}
            </span>
            {isExpanded ? (
                <ChevronDown size={20} style={{ color: 'var(--color-text-tertiary)' }} />
            ) : (
                <ChevronRight size={20} style={{ color: 'var(--color-text-tertiary)' }} />
            )}
        </button>
        {isExpanded && (
            <div style={{
                padding: '0 var(--space-4) var(--space-4)',
                borderTop: '1px solid var(--color-border)'
            }}>
                {children}
            </div>
        )}
    </div>
);

export default CampaignDetailView;
